import React from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";
import { cn } from "@/lib/utils";

/**
 * Line-by-line mask reveal — wraps each line in overflow:hidden and slides
 * its inner span from y:100% → 0 over 600ms with a 40ms per-line stagger.
 *
 * Provide `lines` as an array, or `children` as a newline-delimited string.
 * Reduced-motion: renders plain text, no animation.
 */
export function MaskReveal({
  as: As = "h2",
  lines,
  className,
  children,
  amount = 0.35,
  once = true,
  delay = 0,
}) {
  const ref = React.useRef(null);
  const inView = useInView(ref, { once, amount });
  const reduce = useReducedMotion();

  const source =
    lines ?? (typeof children === "string" ? children.split("\n") : [children]);

  if (reduce) {
    return (
      <As ref={ref} className={className}>
        {source.map((line, i) => (
          <span key={i} className="block">
            {line}
          </span>
        ))}
      </As>
    );
  }

  return (
    <As ref={ref} className={className}>
      {source.map((line, i) => (
        <span
          key={i}
          className={cn("block overflow-hidden", "leading-[1.1]")}
          aria-hidden={undefined}
        >
          <motion.span
            className="block will-change-transform"
            initial={{ y: "100%" }}
            animate={inView ? { y: "0%" } : { y: "100%" }}
            transition={{
              duration: 0.6,
              ease: [0.2, 0.8, 0.2, 1],
              delay: (delay + i * 40) / 1000,
            }}
          >
            {line}
          </motion.span>
        </span>
      ))}
    </As>
  );
}

export default MaskReveal;
